import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import SeoHead from "../components/SeoHead";
import { CONTACT, whatsappLink } from "../data/content";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error("Please add your name and message"); return; }
    window.open(whatsappLink(`Contact from ${form.name} (${form.phone}): ${form.message}`), "_blank");
    toast.success("Opening WhatsApp...");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <SeoHead title="Contact ApnaFix" description="Get in touch with ApnaFix for local home services, partnerships and support." />
      <h1 className="font-display text-3xl font-extrabold text-slate-900">Contact Us</h1>
      <p className="mt-2 text-slate-600">We're here to help. Reach us any way you like.</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-8">
        <div className="space-y-3">
          {[
            { Icon: Phone, label: "Call us", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-call" },
            { Icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: whatsappLink(), testid: "contact-whatsapp" },
            { Icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
          ].map(({ Icon, label, value, href, testid }) => (
            <a key={label} href={href} target="_blank" rel="noreferrer" data-testid={testid}
               className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-orange-400 hover:shadow-md transition-all">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-orange-50 text-brand-orange"><Icon className="w-5 h-5" /></span>
              <div><p className="text-xs text-slate-500">{label}</p><p className="font-semibold text-slate-900">{value}</p></div>
            </a>
          ))}
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-orange-50 text-brand-orange"><MapPin className="w-5 h-5" /></span>
            <div><p className="text-xs text-slate-500">Service areas</p><p className="font-semibold text-slate-900">Kandivali • Malad • Borivali • Goregaon</p></div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 h-max">
          <h2 className="font-display font-bold text-lg text-slate-900">Send us a message</h2>
          <div><Label className="text-sm">Name</Label><Input data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" placeholder="Your name" /></div>
          <div><Label className="text-sm">Phone</Label><Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11" placeholder="Mobile number" /></div>
          <div><Label className="text-sm">Message</Label><Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5" rows={4} placeholder="How can we help?" /></div>
          <Button type="submit" data-testid="contact-submit-btn" className="w-full bg-brand-orange hover:bg-orange-600 text-white font-semibold h-11">Send via WhatsApp <Send className="w-4 h-4 ml-1" /></Button>
        </form>
      </div>
    </div>
  );
}
